// Attack styles (GDD §8, user 2026-09-27: every unit fires its own way): one volley generator per style turns one
// attack (match/attacks.ts AttackView) into projectiles (ProjectileSystem) and muzzle effects. The procedural parts:
//   spray   — conscripts: two bolts per body, each aimed off target by a Gaussian (Box–Muller) scatter proportional to
//             range, fired at random through the volley: storm-trooper spread fire.
//   cannon  — tank: one heavy shell on a low parabola, muzzle flash.
//   barrage — artillery: three high lobs that land on a sunflower (golden-angle) pattern filling the splash radius.
//   homing  — mobile SAM and SAM Site: missiles that climb, snake on a random sine and dive onto the aircraft.
//   strafe  — fighter: two parallel tracer streams whose rounds walk along the line of fire across the target.
//   carpet  — bomber: a stick of bombs falling from altitude onto points spaced along the bomber's heading.
//   swarm   — drones: one laser per drone, its origin dancing on a Lissajous figure so the beams criss-cross.
//   salvo   — destroyer: a twin-barrel salvo of shells, the second a beat later.
//   turret  — city defence guns: a three-round burst with a small scatter.
// Every size is a multiple of the firing unit's glyph size or of the shot's range, so a volley reads the same at any
// zoom and any theatre scale. Randomness is presentation-only (Math.random): the sim never sees it.

import { Vector3 } from 'three'
import { BUILDING_KEYS, UNITS, UNIT_KEYS, keyOf, type BuildingKey, type UnitKey } from '@taxwar/content'
import { InvariantViolation, NEUTRAL_OWNER, type OwnerValue } from '@taxwar/net'
import type { Combatant } from '../match/contracts'
import type { EffectIdFor } from './effects'
import { tangentAt } from './paths'
import { Launch, type ProjectileDef } from './ProjectileSystem'

export const ATTACK_STYLES = ['spray', 'cannon', 'barrage', 'homing', 'strafe', 'carpet', 'swarm', 'salvo', 'turret'] as const
export type AttackStyle = (typeof ATTACK_STYLES)[number]

/** What the VFX layer offers a volley. */
export interface VolleyOutput {
  launch(launch: Launch): void
  /** Point effects on the globe at world position `at`. */
  point(ids: readonly EffectIdFor<'point'>[], owner: OwnerValue, at: Vector3): void
}

/** One attack in world terms, filled by the VFX layer before a style runs. */
export class Volley {
  owner: OwnerValue = NEUTRAL_OWNER
  /** World positions of the shooter's muzzle (its glyph base) and of the target on the network. */
  readonly from = new Vector3()
  readonly to = new Vector3()
  /** Unit tangents at the shooter: its course (pose heading), its line of fire, and the right of the line of fire. */
  readonly course = new Vector3()
  readonly heading = new Vector3()
  readonly side = new Vector3()
  /** The firing unit's glyph size and the shot's range, render units. */
  unitSize = 0
  range = 0
  /** The weapon's splash radius, render units (0 for none). */
  splash = 0
  bodies = 1
  /** Layer time, seconds (the Lissajous dance of drone lasers follows it). */
  time = 0
}

// ------------------------------------------------------------------------------------------------ projectiles

const BOLT: ProjectileDef = { trajectory: 'bolt', secondsPerUnit: 3.2, minSeconds: 0.08, maxSeconds: 0.3, streak: 0.35, lift: 0, widthPx: 1.6, core: 0.8, dashesPerUnit: 0, colour: 'owner', intensity: 1.8, fade: 0.06, impact: ['hitSpark'] }
const TRACER: ProjectileDef = { ...BOLT, secondsPerUnit: 2.4, streak: 0.45, widthPx: 1.3, intensity: 1.6, impact: [] }
const TURRET_ROUND: ProjectileDef = { ...BOLT, widthPx: 1.8, colour: 'spark', intensity: 1.4 }
const SHELL: ProjectileDef = { trajectory: 'shell', secondsPerUnit: 5, minSeconds: 0.25, maxSeconds: 0.6, streak: 0.2, lift: 0.06, widthPx: 2.6, core: 1, dashesPerUnit: 0, colour: 'spark', intensity: 1.6, fade: 0.1, impact: ['shellBlast', 'shellDebris'] }
const NAVAL_SHELL: ProjectileDef = { ...SHELL, lift: 0.12, widthPx: 2.2 }
const LOB: ProjectileDef = { ...SHELL, secondsPerUnit: 9, minSeconds: 0.7, maxSeconds: 1.4, streak: 0.12, lift: 0.45, widthPx: 2.4 }
const MISSILE: ProjectileDef = { trajectory: 'homing', secondsPerUnit: 7, minSeconds: 0.45, maxSeconds: 1.1, streak: 0.3, lift: 0.35, widthPx: 1.8, core: 0.6, dashesPerUnit: 0, colour: 'owner', intensity: 1.5, fade: 0.2, impact: ['flakBurst', 'flakSparks'] }
const BOMB: ProjectileDef = { trajectory: 'bomb', secondsPerUnit: 4, minSeconds: 0.45, maxSeconds: 0.8, streak: 0.15, lift: 0, widthPx: 2.4, core: 1, dashesPerUnit: 0, colour: 'hot', intensity: 1.2, fade: 0.08, impact: ['bombBlast', 'bombFire'] }
const LASER: ProjectileDef = { trajectory: 'beam', secondsPerUnit: 0, minSeconds: 0.14, maxSeconds: 0.14, streak: 1, lift: 0, widthPx: 1.1, core: 1, dashesPerUnit: 0, colour: 'owner', intensity: 2, fade: 0.06, impact: [] }

const MUZZLE: readonly EffectIdFor<'point'>[] = ['muzzleFlash']
const LASER_HIT: readonly EffectIdFor<'point'>[] = ['hitSpark']

// ------------------------------------------------------------------------------------------------ the math

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))
const TAU = Math.PI * 2

/** Standard normal sample (Box–Muller); 1 − random keeps the log finite. */
function gaussian(): number {
  return Math.sqrt(-2 * Math.log(1 - Math.random())) * Math.cos(TAU * Math.random())
}

function between(min: number, max: number): number {
  return min + (max - min) * Math.random()
}

const east = new Vector3()
const north = new Vector3()
const normal = new Vector3()

/** `out` = `at` moved by (x, y) render units across the globe's surface there (x east, y north), at `at`'s radius. */
function shiftOnSphere(at: Vector3, x: number, y: number, out: Vector3): Vector3 {
  const radius = at.length()
  normal.copy(at).divideScalar(radius)
  tangentAt(normal, east)
  north.crossVectors(normal, east)
  return out.copy(at).addScaledVector(east, x).addScaledVector(north, y).normalize().multiplyScalar(radius)
}

/** `out` = `at` moved by `x` render units along the unit tangent `axis`, back at `at`'s radius. */
function shiftAlong(at: Vector3, axis: Vector3, x: number, out: Vector3): Vector3 {
  const radius = at.length()
  return out.copy(at).addScaledVector(axis, x).normalize().multiplyScalar(radius)
}

// ------------------------------------------------------------------------------------------------ the styles

const launch = new Launch()
const muzzle = new Vector3()
const target = new Vector3()

/** Launches `def` from `from` to `to` after `delay`; a non-zero `wobble` snakes a homing missile. */
function fire(out: VolleyOutput, volley: Volley, def: ProjectileDef, from: Vector3, to: Vector3, delay: number, wobble = 0): void {
  launch.def = def
  launch.owner = volley.owner
  launch.from.copy(from)
  launch.to.copy(to)
  launch.delay = delay
  launch.wobble = wobble
  launch.wobbleCycles = wobble === 0 ? 0 : between(1.2, 2.4)
  launch.wobblePhase = wobble === 0 ? 0 : Math.random() * TAU
  out.launch(launch)
}

const SPRAY_BOLTS_PER_BODY = 2
const SPRAY_WINDOW_SEC = 0.4
/** Conscript aim: Gaussian scatter of the landing point, as a fraction of the range (and of the glyph at the muzzle). */
const SPRAY_SPREAD = 0.16
const SPRAY_MUZZLE_SPREAD = 0.35
const BARRAGE_SHELLS = 3
const BARRAGE_INTERVAL_SEC = 0.16
const MISSILES = 2
const MISSILE_INTERVAL_SEC = 0.2
const STRAFE_LANES: readonly number[] = [-0.3, 0.3]
const STRAFE_ROUNDS = 4
const STRAFE_INTERVAL_SEC = 0.05
const STRAFE_WALK = 0.3
const CARPET_BOMBS = 5
const CARPET_INTERVAL_SEC = 0.1
const CARPET_SPACING = 0.45
const SWARM_DANCE = 0.6
const SWARM_INTERVAL_SEC = 0.03
const SWARM_SCATTER = 0.2
/** Twin barrels: lateral offset (glyph sizes) and delay of each. */
const SALVO_BARRELS: readonly (readonly [number, number])[] = [
  [-0.25, 0],
  [0.25, 0.12],
]
const SALVO_SCATTER = 0.25
const TURRET_ROUNDS = 3
const TURRET_INTERVAL_SEC = 0.08
const TURRET_SPREAD = 0.06

const STYLES: Readonly<Record<AttackStyle, (volley: Volley, out: VolleyOutput) => void>> = {
  spray: (volley, out) => {
    const muzzleSpread = SPRAY_MUZZLE_SPREAD * volley.unitSize
    const spread = SPRAY_SPREAD * volley.range
    for (let body = 0; body < volley.bodies; body++) {
      for (let shot = 0; shot < SPRAY_BOLTS_PER_BODY; shot++) {
        shiftOnSphere(volley.from, gaussian() * muzzleSpread, gaussian() * muzzleSpread, muzzle)
        shiftOnSphere(volley.to, gaussian() * spread, gaussian() * spread, target)
        fire(out, volley, BOLT, muzzle, target, Math.random() * SPRAY_WINDOW_SEC)
      }
    }
  },
  cannon: (volley, out) => {
    out.point(MUZZLE, volley.owner, volley.from)
    fire(out, volley, SHELL, volley.from, volley.to, 0)
  },
  barrage: (volley, out) => {
    out.point(MUZZLE, volley.owner, volley.from)
    // Sunflower (golden-angle) spacing: n points evenly filling a disc of the splash radius, randomly turned.
    const rotation = Math.random() * TAU
    for (let i = 0; i < BARRAGE_SHELLS; i++) {
      const r = volley.splash * Math.sqrt((i + 0.5) / BARRAGE_SHELLS)
      const theta = rotation + i * GOLDEN_ANGLE
      shiftOnSphere(volley.to, r * Math.cos(theta), r * Math.sin(theta), target)
      fire(out, volley, LOB, volley.from, target, i * BARRAGE_INTERVAL_SEC)
    }
  },
  homing: (volley, out) => {
    for (let i = 0; i < MISSILES; i++) {
      const wobble = between(0.08, 0.16) * (i % 2 === 0 ? 1 : -1)
      fire(out, volley, MISSILE, volley.from, volley.to, i * MISSILE_INTERVAL_SEC, wobble)
    }
  },
  strafe: (volley, out) => {
    for (const lane of STRAFE_LANES) {
      shiftAlong(volley.from, volley.side, lane * volley.unitSize, muzzle)
      for (let round = 0; round < STRAFE_ROUNDS; round++) {
        // Rounds walk along the line of fire across the target.
        shiftAlong(volley.to, volley.heading, (round - (STRAFE_ROUNDS - 1) / 2) * STRAFE_WALK * volley.unitSize, target)
        shiftAlong(target, volley.side, lane * volley.unitSize, target)
        fire(out, volley, TRACER, muzzle, target, round * STRAFE_INTERVAL_SEC)
      }
    }
  },
  carpet: (volley, out) => {
    // The stick lands along the bomber's course, centred on the target.
    const spacing = Math.max(volley.splash, volley.unitSize) * CARPET_SPACING
    for (let i = 0; i < CARPET_BOMBS; i++) {
      shiftAlong(volley.to, volley.course, (i - (CARPET_BOMBS - 1) / 2) * spacing, target)
      fire(out, volley, BOMB, volley.from, target, i * CARPET_INTERVAL_SEC)
    }
  },
  swarm: (volley, out) => {
    // Each drone's muzzle dances on a 3:2 Lissajous figure, phase-shifted per drone, so the beams criss-cross.
    const dance = SWARM_DANCE * volley.unitSize
    const scatter = SWARM_SCATTER * volley.unitSize
    for (let body = 0; body < volley.bodies; body++) {
      const phase = (body * TAU) / volley.bodies
      shiftOnSphere(volley.from, dance * Math.sin(3 * volley.time + phase), dance * Math.sin(2 * volley.time + 0.7 * phase), muzzle)
      shiftOnSphere(volley.to, gaussian() * scatter, gaussian() * scatter, target)
      fire(out, volley, LASER, muzzle, target, body * SWARM_INTERVAL_SEC)
    }
    out.point(LASER_HIT, volley.owner, volley.to)
  },
  salvo: (volley, out) => {
    out.point(MUZZLE, volley.owner, volley.from)
    const scatter = SALVO_SCATTER * volley.unitSize
    for (const [lateral, delay] of SALVO_BARRELS) {
      shiftAlong(volley.from, volley.side, lateral * volley.unitSize, muzzle)
      shiftOnSphere(volley.to, gaussian() * scatter, gaussian() * scatter, target)
      fire(out, volley, NAVAL_SHELL, muzzle, target, delay)
    }
  },
  turret: (volley, out) => {
    out.point(MUZZLE, volley.owner, volley.from)
    const spread = TURRET_SPREAD * volley.range
    for (let round = 0; round < TURRET_ROUNDS; round++) {
      shiftOnSphere(volley.to, gaussian() * spread, gaussian() * spread, target)
      fire(out, volley, TURRET_ROUND, volley.from, target, round * TURRET_INTERVAL_SEC)
    }
  },
}

// ------------------------------------------------------------------------------------------------ who fires how

/** Each unit type's style; null for units without a weapon (the carrier launches fighters instead). */
const UNIT_STYLES: Readonly<Record<UnitKey, AttackStyle | null>> = {
  conscripts: 'spray',
  tank: 'cannon',
  artillery: 'barrage',
  mobile_sam: 'homing',
  fighter: 'strafe',
  bomber: 'carpet',
  drone_swarm: 'swarm',
  destroyer: 'salvo',
  carrier: null,
}

/** Buildings with a weapon of their own; null for the rest. */
const BUILDING_STYLES: Readonly<Record<BuildingKey, AttackStyle | null>> = {
  tax_office: null,
  airbase: null,
  port: null,
  sam_site: 'homing',
  fortress: null,
  propaganda_office: null,
}

type KindOf<K extends Combatant['kind']> = Extract<Combatant, { readonly kind: K }>

const SOURCE_STYLES: { readonly [K in Combatant['kind']]: (source: KindOf<K>) => AttackStyle | null } = {
  unit: (source) => UNIT_STYLES[keyOf(UNIT_KEYS, source.unitType)],
  city: () => 'turret',
  building: (source) => BUILDING_STYLES[keyOf(BUILDING_KEYS, source.buildingType)],
}

/** Splash radius of the source's weapon in map units (content); structures have none. */
const SOURCE_SPLASH_MU: { readonly [K in Combatant['kind']]: (source: KindOf<K>) => number } = {
  unit: (source) => UNITS[keyOf(UNIT_KEYS, source.unitType)].splashRadius,
  city: () => 0,
  building: () => 0,
}

export function attackStyleOf(source: Combatant): AttackStyle {
  const style = (SOURCE_STYLES[source.kind] as (source: Combatant) => AttackStyle | null)(source)
  if (style === null) throw new InvariantViolation(`a ${source.kind} without a weapon fired`)
  return style
}

export function splashMuOf(source: Combatant): number {
  return (SOURCE_SPLASH_MU[source.kind] as (source: Combatant) => number)(source)
}

export function playVolley(style: AttackStyle, volley: Volley, out: VolleyOutput): void {
  STYLES[style](volley, out)
}
